import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CircularProgress, Box } from "@mui/material";
import { getMe } from "../../features/auth";
import { qk } from "../../lib";
import { ROUTES } from "../utils";
import { useCapabilities } from "../../hooks/useCapabilities";
import useMasterData from "../../hooks/useMasterData";

export const ProtectedRoute: React.FC = () => {

  const location = useLocation();
  const { canRoute } = useCapabilities();
  const { isLoading: isMasterDataLoading } = useMasterData();

  const { pathname } = useLocation();

  const isPublicRoute = pathname.startsWith("/auth") || pathname.startsWith("/dashboard");

  const { data: session, isLoading, isError } = useQuery({
    queryKey: qk.me(),
    queryFn: getMe
  });

  // While session is loading
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (session && session.authenticated) {
    // If authenticated but master data is still loading, show loading spinner
    if (isMasterDataLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
  }

  // Not authenticated or error
  if (isError || !session?.authenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Needs password reset
  if (session.needs_new_password) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // redirect root path '/' → dashboard
  if (location.pathname === "/") {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  // Authenticated but no capability to access the route
  if (!isMasterDataLoading && !isPublicRoute && !canRoute(pathname)) {
    return <Navigate to={ROUTES.NOT_FOUND} replace />;
  }

  // Authenticated → render children
  return <Outlet />;
};